import { onAuthStateChanged, signInWithCustomToken, signOut } from 'firebase/auth';
import { auth } from './index';

/**
 * The Firebase session behind API calls.
 *
 * A session made with Firebase's own sign-in (Super Admin email/password, or a
 * company Admin's dashboard sign-in by custom token) refreshes itself, so
 * `currentIdToken()` asks Firebase for the token instead of trusting the copy in
 * localStorage, which is dead an hour after sign-in. The legacy extension
 * hand-off (`/admin/<token>/<email>`) has no Firebase session, so it keeps
 * using the stored token until it expires.
 */

// Firebase 9 has no `authStateReady()`: wait for the first auth event, which
// is when a session saved by an earlier visit has been restored (or not).
const authReady: Promise<void> = new Promise((resolve) => {
  const unsubscribe = onAuthStateChanged(auth, () => {
    unsubscribe();
    resolve();
  });
});

export async function currentIdToken(): Promise<string | null> {
  await authReady;
  if (auth.currentUser) {
    try {
      const token = await auth.currentUser.getIdToken();
      localStorage.setItem('token', token);
      return token;
    } catch (error) {
      console.warn('Could not refresh the sign-in token:', error);
    }
  }
  return localStorage.getItem('token');
}

/** Sign in with a custom token from the server; returns a fresh ID token. */
export async function signInWithServerToken(customToken: string): Promise<string> {
  const credential = await signInWithCustomToken(auth, customToken);
  const token = await credential.user.getIdToken();
  localStorage.setItem('token', token);
  return token;
}

/** Forget the session here and in Firebase, so the next visit doesn't restore it. */
export async function signOutEverywhere(): Promise<void> {
  localStorage.clear();
  try {
    await signOut(auth);
  } catch (error) {
    console.warn('Firebase sign-out failed:', error);
  }
}
